import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { FiZoomIn, FiZoomOut, FiRotateCw, FiCheck, FiX } from 'react-icons/fi';

const ImageCropper = ({
    isOpen,
    imageSrc,
    onCropComplete,
    onCancel,
    title = 'Crop Image'
}) => {
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [scale, setScale] = useState(1);
    const [rotation, setRotation] = useState(0);
    const imgRef = useRef(null);
    const canvasRef = useRef(null);

    const onImageLoad = useCallback((e) => {
        imgRef.current = e.currentTarget;

        // Set initial crop to cover 80% of the image
        const { width, height } = e.currentTarget;
        const size = Math.min(width, height) * 0.8;
        const x = (width - size) / 2;
        const y = (height - size) / 2;

        setCrop({
            unit: 'px',
            x,
            y,
            width: size,
            height: size,
        });
    }, []);

    const getCroppedImg = useCallback(() => {
        if (!completedCrop || !imgRef.current) {
            return null;
        }

        const canvas = canvasRef.current;
        const image = imgRef.current;
        const ctx = canvas.getContext('2d');

        // Calculate the actual crop dimensions
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        const cropX = completedCrop.x * scaleX;
        const cropY = completedCrop.y * scaleY;
        const cropWidth = completedCrop.width * scaleX;
        const cropHeight = completedCrop.height * scaleY;

        // Set canvas size to crop size
        canvas.width = cropWidth;
        canvas.height = cropHeight;

        // Apply transformations
        ctx.save();
        ctx.scale(scale, scale);
        ctx.rotate((rotation * Math.PI) / 180);

        // Draw the cropped image
        ctx.drawImage(
            image,
            cropX / scale,
            cropY / scale,
            cropWidth / scale,
            cropHeight / scale,
            0,
            0,
            cropWidth,
            cropHeight
        );
        ctx.restore();

        // Convert canvas to blob
        return new Promise((resolve) => {
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        // Create a File object from blob
                        const file = new File([blob], 'cropped-image.jpg', {
                            type: 'image/jpeg',
                            lastModified: Date.now(),
                        });
                        resolve(file);
                    }
                },
                'image/jpeg',
                0.9 // Quality
            );
        });
    }, [completedCrop, scale, rotation]);

    const handleCropComplete = async () => {
        const croppedFile = await getCroppedImg();
        if (croppedFile && onCropComplete) {
            onCropComplete(croppedFile);
        }
    };

    const handleZoomIn = () => setScale((s) => Math.min(s + 0.1, 2));
    const handleZoomOut = () => setScale((s) => Math.max(s - 0.1, 0.5));
    const handleRotate = () => setRotation((r) => (r + 90) % 360);

    const handleReset = () => {
        setScale(1);
        setRotation(0);
        setCompletedCrop(null);
    };

    if (!isOpen || !imageSrc) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-2 sm:p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl h-full sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-3 sm:p-4 border-b flex-shrink-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 truncate pr-2">{title}</h3>
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        {/* Zoom Controls */}
                        <button
                            onClick={handleZoomOut}
                            className="p-1.5 sm:p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                            title="Zoom Out"
                        >
                            <FiZoomOut className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <span className="text-xs sm:text-sm text-gray-500 min-w-[2.5rem] sm:min-w-[3rem] text-center">
                            {Math.round(scale * 100)}%
                        </span>
                        <button
                            onClick={handleZoomIn}
                            className="p-1.5 sm:p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                            title="Zoom In"
                        >
                            <FiZoomIn className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>

                        {/* Rotate Button */}
                        <button
                            onClick={handleRotate}
                            className="p-1.5 sm:p-2 text-gray-600 hover:bg-gray-100 rounded-lg sm:ml-2"
                            title="Rotate 90°"
                        >
                            <FiRotateCw className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>

                        {/* Reset Button - Hidden on mobile */}
                        <button
                            onClick={handleReset}
                            className="hidden sm:block px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg ml-2"
                        >
                            Reset
                        </button>
                    </div>
                </div>

                {/* Cropper Area */}
                <div className="flex-1 overflow-auto p-2 sm:p-4 bg-gray-100 flex items-center justify-center min-h-0">
                    <ReactCrop
                        crop={crop}
                        onChange={(c) => setCrop(c)}
                        onComplete={(c) => setCompletedCrop(c)}
                        className="max-w-full max-h-full"
                    >
                        <img
                            src={imageSrc}
                            alt="Crop preview"
                            onLoad={onImageLoad}
                            style={{
                                transform: `scale(${scale}) rotate(${rotation}deg)`,
                                transformOrigin: 'center center',
                                maxWidth: '100%',
                                maxHeight: '50vh',
                                width: 'auto',
                                height: 'auto',
                            }}
                        />
                    </ReactCrop>
                </div>

                {/* Footer */}
                <div className="flex flex-col sm:flex-row items-center justify-between p-3 sm:p-4 border-t bg-gray-50 gap-2 sm:gap-0 flex-shrink-0">
                    <p className="hidden sm:block text-sm text-gray-500">
                        Drag to adjust crop area. Use controls above to zoom/rotate.
                    </p>
                    {/* Mobile Reset Button */}
                    <button
                        onClick={handleReset}
                        className="sm:hidden text-sm text-gray-600 hover:text-gray-800"
                    >
                        Reset
                    </button>
                    <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                        <button
                            onClick={onCancel}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm sm:text-base"
                        >
                            <FiX className="w-4 h-4" />
                            Cancel
                        </button>
                        <button
                            onClick={handleCropComplete}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
                        >
                            <FiCheck className="w-4 h-4" />
                            Crop & Save
                        </button>
                    </div>
                </div>
            </div>

            {/* Hidden canvas for image processing */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
    );
};

export default ImageCropper;